import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/context/BillingContext";
import { useEffect, useState } from "react";
import { Check, ArrowLeft, ArrowRight, Hourglass } from "lucide-react";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/track";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import PromoBanner, { PROMO_CONFIG } from "@/components/PromoBanner";
import { toast } from "sonner";

const sharedFeatures = [
  "Profit & utilization tracking",
  "Retainer health status",
  "CSV import & export",
  "Monthly snapshots",
  "Client notes & service-type tags",
  "Multi-currency support",
  "Activity timeline",
  "14-day free trial",
];

const plans = [
  {
    id: "solo",
    name: "Solo",
    price: 19,
    blurb: "For consultants and fractional operators managing a handful of monthly retainers.",
    headline_feature: "Up to 5 active retainers",
    extras: [],
    cta: "Start with Solo",
    highlight: false,
    testid: "plan-solo",
  },
  {
    id: "studio",
    name: "Studio",
    price: 49,
    blurb: "For studios and small teams that need a clear view of client profit.",
    headline_feature: "Up to 15 active retainers",
    extras: [],
    cta: "Choose Studio",
    highlight: true,
    testid: "plan-studio",
  },
  {
    id: "agency",
    name: "Agency",
    price: 99,
    blurb: "For growing agencies with more retained clients.",
    headline_feature: "Up to 40 active retainers",
    extras: ["Priority email support"],
    cta: "Choose Agency",
    highlight: false,
    testid: "plan-agency",
  },
  {
    id: "scale",
    name: "Scale",
    price: 179,
    blurb: "For larger teams that manage many retained clients.",
    headline_feature: "Up to 100 active retainers",
    extras: ["Priority email support", "Early access to new features"],
    cta: "Choose Scale",
    highlight: false,
    testid: "plan-scale",
  },
];

const PricingPage = () => {
  const { user } = useAuth();
  const { subscription } = useBilling();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);

  const currentPlanId = subscription?.plan_id;

  useEffect(() => {
    trackEvent("pricing_viewed");
  }, []);

  const startLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/pricing";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePlanClick = async (planId) => {
    trackEvent("plan_selected", { plan_id: planId });
    if (!user) {
      startLogin();
      return;
    }
    if (currentPlanId === planId) {
      navigate("/dashboard");
      return;
    }
    setLoadingPlan(planId);
    try {
      trackEvent("checkout_opened", { plan_id: planId });
      const r = await api.post("/billing/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      // Redirect to Stripe
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoadingPlan(null);
    }
  };

  const showMarketingHeader = !user;

  return (
    <div className={user ? "" : "min-h-screen bg-[#F8F6F1]"}>
      {showMarketingHeader ? <PromoBanner /> : null}
      {showMarketingHeader ? (
        <header className="sticky top-0 z-30 backdrop-blur-md bg-[#F8F6F1]/85 border-b border-[#E4DFD2]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5" data-testid="pricing-logo">
              <Logo size={28} showTagline />
            </Link>
            <div className="flex items-center gap-1">
              {!user ? (
                <button
                  type="button"
                  onClick={startLogin}
                  data-testid="pricing-login-btn"
                  className="px-3 py-2 text-sm text-[#1F2326] hover:text-[#B89B5E] font-medium transition-colors"
                >
                  Log in
                </button>
              ) : null}
              <Link
                to="/"
                data-testid="pricing-back"
                className="inline-flex items-center gap-1 text-sm text-[#69747C] hover:text-[#1F2326] px-3 py-2"
              >
                <ArrowLeft className="w-4 h-4" /> Back home
              </Link>
            </div>
          </div>
        </header>
      ) : null}

      <div className={`max-w-7xl mx-auto px-6 ${user ? "" : "py-16 lg:py-24"}`} data-testid="pricing-page">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-[11px] tracking-[0.1em] uppercase font-semibold text-[#B89B5E]">Pricing</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-[#1F2326] mt-2">
            Simple pricing based on how many retainers you track.
          </h1>
          <p className="text-[#69747C] mt-4 text-base leading-relaxed">
            Start small. Add more clients as your retainer book grows. No per-seat fees.
          </p>
          <p className="text-[#69747C] mt-3 text-sm">14-day free trial. No credit card needed.</p>
          {subscription?.status === "trialing" && user ? (
            <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F0EADA] border border-[#E4DFD2] text-[#7A5E1F] text-xs font-medium">
              <Hourglass className="w-3 h-3" />
              {subscription.days_left} days left in trial
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {plans.map((p) => {
            const isCurrent = currentPlanId === p.id;
            const isLoading = loadingPlan === p.id;
            return (
              <div
                key={p.id}
                data-testid={p.testid}
                className={`relative bg-white rounded-2xl p-7 flex flex-col ${
                  p.highlight
                    ? "border-2 border-[#B89B5E] shadow-[0_24px_50px_-20px_rgba(184,155,94,0.35)] lg:-translate-y-3"
                    : "border border-[#E4DFD2]"
                } ${isCurrent ? "ring-2 ring-[#42624B] ring-offset-2 ring-offset-[#F8F6F1]" : ""}`}
              >
                {p.highlight ? (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#B89B5E] text-[#1F2326] text-[11px] font-semibold px-3 py-1 rounded-full tracking-wide">
                    MOST POPULAR
                  </div>
                ) : null}

                <div className="flex items-center justify-between">
                  <div className="font-display text-xl font-semibold text-[#1F2326]">{p.name}</div>
                  {isCurrent ? (
                    <span className="text-[10px] uppercase tracking-wider font-semibold bg-[#EFF3EE] text-[#42624B] border border-[#C5D5C8] px-2 py-0.5 rounded-full">
                      Current
                    </span>
                  ) : null}
                </div>
                <p className="text-sm text-[#69747C] mt-1 min-h-[40px]">{p.blurb}</p>

                <div className="flex items-baseline gap-2 mt-6">
                  {PROMO_CONFIG.enabled && PROMO_CONFIG.endsAt.getTime() > Date.now() ? (
                    <>
                      <span
                        className="font-display text-5xl font-semibold tracking-tighter text-[#1F2326]"
                        data-testid={`plan-${p.id}-discounted-price`}
                      >
                        ${(p.price * (1 - PROMO_CONFIG.percent / 100)).toFixed(2)}
                      </span>
                      <span className="text-[#69747C] text-sm">/month</span>
                      <span className="text-[#A9A294] text-base line-through ml-1" data-testid={`plan-${p.id}-original-price`}>
                        ${p.price}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="font-display text-5xl font-semibold tracking-tighter text-[#1F2326]">
                        ${p.price}
                      </span>
                      <span className="text-[#69747C] text-sm">/month</span>
                    </>
                  )}
                </div>
                {PROMO_CONFIG.enabled && PROMO_CONFIG.endsAt.getTime() > Date.now() ? (
                  <div className="mt-2 text-[11px] text-[#7A5E1F] bg-[#F0EADA] border border-[#E4DFD2] rounded-md px-2 py-1 inline-block font-medium">
                    Save ${(p.price * (PROMO_CONFIG.percent / 100)).toFixed(2)} / month with code{" "}
                    <span className="font-mono font-semibold text-[#1F2326]">{PROMO_CONFIG.code}</span>
                  </div>
                ) : null}

                <ul className="mt-6 space-y-3 flex-1">
                  <li className="flex items-start gap-2.5 text-sm text-[#1F2326] font-semibold">
                    <Check
                      className={`w-4 h-4 mt-0.5 flex-none ${p.highlight ? "text-[#B89B5E]" : "text-[#1F2326]"}`}
                      strokeWidth={2.5}
                    />
                    {p.headline_feature}
                  </li>
                  {p.extras.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-[#1F2326]">
                      <Check
                        className={`w-4 h-4 mt-0.5 flex-none ${p.highlight ? "text-[#B89B5E]" : "text-[#1F2326]"}`}
                        strokeWidth={2.5}
                      />
                      {f}
                    </li>
                  ))}
                  <li className="text-xs text-[#69747C] pt-2 border-t border-[#EDE7D6] mt-3">
                    Includes everything in the section below.
                  </li>
                </ul>

                <button
                  type="button"
                  onClick={() => handlePlanClick(p.id)}
                  disabled={isLoading || isCurrent}
                  data-testid={`${p.testid}-cta`}
                  className={`mt-8 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors disabled:opacity-60 ${
                    isCurrent
                      ? "bg-[#EFF3EE] text-[#42624B] border border-[#C5D5C8] cursor-default"
                      : p.highlight
                      ? "bg-[#B89B5E] text-[#1F2326] hover:bg-[#A78946]"
                      : "bg-[#1F2326] text-[#F8F6F1] hover:bg-[#2A2E32]"
                  }`}
                >
                  {isCurrent ? "Current plan" : isLoading ? "Opening checkout..." : p.cta}
                  {!isCurrent && !isLoading ? <ArrowRight className="w-3.5 h-3.5" /> : null}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-16 max-w-3xl mx-auto" data-testid="every-plan-includes">
          <div className="text-center mb-6">
            <div className="text-[11px] tracking-[0.1em] uppercase font-semibold text-[#B89B5E]">
              Every plan includes
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tighter text-[#1F2326] mt-2">
              The full toolkit, on every tier.
            </h2>
            <p className="text-sm text-[#69747C] mt-2">
              Higher tiers just let you track more retainers.
            </p>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 bg-white border border-[#E4DFD2] rounded-2xl p-7">
            {sharedFeatures.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-[#1F2326]">
                <Check className="w-4 h-4 mt-0.5 flex-none text-[#42624B]" strokeWidth={2.5} />
                {f}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-12 text-center text-xs text-[#69747C]">
          Prices in USD. Switch or cancel any time from your billing portal.
        </div>
      </div>
      {showMarketingHeader ? <Footer /> : null}
    </div>
  );
};

export default PricingPage;
